import { Sparkles } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { PlatformProfile } from "../mockData";

// ─── AI insight block ─────────────────────────────────────────────────────────

type InsVariant = "ig" | "ib" | "ia";

const INS_STYLE: Record<InsVariant, { icon: React.ElementType; color: string }> = {
  ig: { icon: ({ size }: { size: number }) => <SparklesIcon size={size} />, color: "#639922" },
  ib: { icon: ({ size }: { size: number }) => <InfoIcon size={size} />,     color: "#378ADD" },
  ia: { icon: ({ size }: { size: number }) => <WarnIcon size={size} />,     color: "#BA7517" },
};

function SparklesIcon({ size }: { size: number }) {
  return <Sparkles size={size} />;
}
function InfoIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  );
}
function WarnIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function Ins({ variant, text }: { variant: InsVariant; text: string }) {
  const { icon: Icon, color } = INS_STYLE[variant];
  return (
    <div className="flex items-start gap-[5px] px-[10px] py-[7px] rounded-md mt-[10px] text-[11px]"
      style={{ background: "rgba(28,28,28,0.04)", color: "rgba(28,28,28,0.55)" }}>
      <span style={{ color, flexShrink: 0, marginTop: 1 }}>
        <Icon size={12} />
      </span>
      <span className="leading-snug">{text}</span>
    </div>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-ink-40 mb-2">{children}</div>
  );
}

// ─── Gender donut ─────────────────────────────────────────────────────────────

function GenderDonut({ malePct, platform }: { malePct: number; platform: string }) {
  const femalePct = 100 - malePct;
  const r = 33; const stroke = 9;
  const circ = 2 * Math.PI * r;
  const maleArc = (malePct / 100) * circ;
  const isTg = platform === "tg";
  const insightText = malePct >= 55
    ? isTg
      ? "Преимущественно мужская — типично для biz/finance аудитории"
      : "Аудитория VK более мужская — выше среднего для этой площадки"
    : malePct <= 45
    ? isTg
      ? "Преимущественно женская аудитория — нетипично для TG finance"
      : "Аудитория VK более женская — отличается от TG-канала"
    : "Сбалансированная аудитория — равные доли М/Ж";

  return (
    <div>
      <SectionLabel>Пол аудитории</SectionLabel>
      <div className="flex items-center gap-6">
        <svg width={86} height={86} viewBox="0 0 86 86">
          <circle cx={43} cy={43} r={r} fill="none" stroke="rgba(28,28,28,0.06)" strokeWidth={stroke} />
          <circle cx={43} cy={43} r={r} fill="none" stroke="#378ADD" strokeWidth={stroke}
            strokeDasharray={`${maleArc} ${circ}`} strokeLinecap="round" transform="rotate(-90 43 43)" />
          <circle cx={43} cy={43} r={r} fill="none" stroke="#ED93B1" opacity={0.85} strokeWidth={stroke}
            strokeDasharray={`${circ - maleArc - 0.5} ${circ}`}
            strokeDashoffset={-(maleArc + 0.5)} strokeLinecap="round" transform="rotate(-90 43 43)" />
        </svg>
        <div className="grid gap-2">
          <div className="flex items-center gap-2 text-[12px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#378ADD" }} />
            <span>Мужчины <span className="font-mono font-bold">{malePct}%</span></span>
          </div>
          <div className="flex items-center gap-2 text-[12px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: "#ED93B1" }} />
            <span>Женщины <span className="font-mono font-bold">{femalePct}%</span></span>
          </div>
        </div>
      </div>
      <Ins variant="ig" text={insightText} />
    </div>
  );
}

// ─── Age grouped bars ─────────────────────────────────────────────────────────

function AgeGroupedBars({ groups, malePct }: { groups: { label: string; male: number; female: number }[]; malePct: number }) {
  const max = Math.max(...groups.flatMap((g) => [g.male, g.female]));
  return (
    <div>
      <SectionLabel>Возраст аудитории</SectionLabel>
      <div className="grid gap-2">
        {groups.map((g) => (
          <div key={g.label} className="flex items-center gap-2">
            <span className="w-[42px] text-[10px] text-ink-40 flex-shrink-0">{g.label}</span>
            <div className="flex-1 flex flex-col gap-[3px]">
              <div className="h-3 rounded-sm overflow-hidden" style={{ background: "rgba(28,28,28,0.06)" }}>
                <div className="h-full rounded-sm" style={{ width: `${(g.male / max) * 100}%`, background: "#378ADD" }} />
              </div>
              <div className="h-3 rounded-sm overflow-hidden" style={{ background: "rgba(28,28,28,0.06)" }}>
                <div className="h-full rounded-sm" style={{ width: `${(g.female / max) * 100}%`, background: "#ED93B1", opacity: 0.7 }} />
              </div>
            </div>
            <span className="w-[30px] text-right text-[10px] font-mono text-ink-40 flex-shrink-0">{g.male + g.female}%</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5 text-[10px] text-ink-40"><span className="w-2 h-2 rounded-full" style={{ background: "#378ADD" }} /> М {malePct}%</div>
        <div className="flex items-center gap-1.5 text-[10px] text-ink-40"><span className="w-2 h-2 rounded-full" style={{ background: "#ED93B1" }} /> Ж {100 - malePct}%</div>
      </div>
    </div>
  );
}

// ─── Geo bars ─────────────────────────────────────────────────────────────────

function GeoBars({ geo }: { geo: { city: string; pct: number }[] }) {
  return (
    <div>
      <SectionLabel>География</SectionLabel>
      <div className="grid gap-2">
        {geo.map((g) => {
          const isRegion = g.city === "Регионы РФ";
          return (
            <div key={g.city} className="flex items-center gap-2.5">
              <span className="w-[120px] text-[12px] text-ink-60 flex-shrink-0 truncate">{g.city}</span>
              <div className="flex-1 h-[5px] rounded-full overflow-hidden" style={{ background: "rgba(28,28,28,0.07)" }}>
                <div className="h-full rounded-full"
                  style={{ width: `${Math.min(100, g.pct * 2.2)}%`, background: isRegion ? "#639922" : "#378ADD" }} />
              </div>
              <span className="w-8 text-right text-[12px] font-[500] font-mono">{g.pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Interests bars ───────────────────────────────────────────────────────────

function InterestBars({ interests, hasCampaignCtx }: {
  interests: { label: string; pct: number; relevant?: boolean }[];
  hasCampaignCtx: boolean;
}) {
  const max = Math.max(...interests.map((i) => i.pct));
  return (
    <div>
      <SectionLabel>Интересы аудитории</SectionLabel>
      <div className="grid gap-2">
        {interests.map((interest) => {
          const relevant = hasCampaignCtx && interest.relevant;
          return (
            <div key={interest.label} className="flex items-center gap-2.5">
              <span className={cn("w-[120px] text-[12px] flex-shrink-0 truncate", relevant ? "font-semibold text-[#27500A]" : "text-ink-60")}>
                {interest.label}
              </span>
              <div className="flex-1 h-[5px] rounded-full overflow-hidden" style={{ background: "rgba(28,28,28,0.07)" }}>
                <div className="h-full rounded-full"
                  style={{ width: `${(interest.pct / max) * 100}%`, background: relevant ? "#639922" : "#378ADD" }} />
              </div>
              <span className="w-8 text-right text-[12px] font-mono text-ink-40">{interest.pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ЦА совпадение ────────────────────────────────────────────────────────────

function CampaignMatchInsight({ productName, pct, platform }: { productName: string; pct: number; platform: string }) {
  const isTg = platform === "tg";
  const text = isTg
    ? `Аудитория совпадает с ЦА ${productName} на ${pct}% — высокий overlap по возрасту 25–34 и профессиональным интересам.`
    : `Аудитория VK совпадает с ЦА ${productName} на ${pct}% — моложе и больше женщин, чем в TG-канале.`;
  return <Ins variant="ig" text={text} />;
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export function AudienceTab({
  data, campaignContext, productName,
}: {
  data: PlatformProfile;
  campaignContext: boolean;
  productName?: string;
}) {
  const overlapPct = 72 + (data.malePct % 20);
  return (
    <div className="grid gap-6">
      <GenderDonut malePct={data.malePct} platform={data.key} />
      <AgeGroupedBars groups={data.ageGroups} malePct={data.malePct} />
      <GeoBars geo={data.geo} />
      <InterestBars interests={data.interests} hasCampaignCtx={campaignContext} />
      {campaignContext && productName && (
        <CampaignMatchInsight productName={productName} pct={overlapPct} platform={data.key} />
      )}
    </div>
  );
}
